import { useState } from "react";
import { Segmented } from "antd";
import { OrderedListOutlined } from "@ant-design/icons";
import { LuClock, LuShoppingBag } from "react-icons/lu";
import useGet from "../../../global/hooks/useGet";
import { GlobalTable } from "../../../global/components/GlobalTable";
import { DeviceLogItemColumns } from "../Page/DeviceLogItemColumns";
import {
  DeviceOrderItemColumns,
  DeviceOrderItemChildColumns,
} from "../Page/DeviceOrderItemColumns";
import usePut from "../../../global/hooks/usePut";
import ConfirmModal from "../../../global/components/ConfirmModal";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";

export const DeviceLogsOrdersPanel = ({
  deviceNo,
  logsLabel = "Device Logs",
  currentOrderOnly = false,
}) => {
  const currentOrderTab = {
    label: "Current Orders",
    value: "currentOrder",
    icon: (
      <span className="relative inline-flex">
        <LuShoppingBag />
        <LuClock className="absolute -bottom-[3px] -right-[4px] text-[9px] bg-white rounded-full" />
      </span>
    ),
  };
  const tabs = currentOrderOnly
    ? [currentOrderTab]
    : [
        { label: logsLabel, value: "deviceLog", icon: <OrderedListOutlined /> },
        { label: "Orders", value: "order", icon: <LuShoppingBag /> },
        currentOrderTab,
      ];
  const [tabValue, setTabValue] = useState(tabs[0].value);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [renewalTarget, setRenewalTarget] = useState(null);

  const { putData: putRenewal } = usePut(
    renewalTarget
      ? `${process.env.REACT_APP_API_URL}/api/orders/${renewalTarget.record.ItemNo}/renewal/`
      : null,
  );
  const onRenewalToggle = (record, checked) =>
    setRenewalTarget({ record, checked });

  const onRenewalConfirm = async () => {
    if (!renewalTarget) return;
    const payload = { Renewal: renewalTarget.checked ? 1 : 0 };
    try {
      await putRenewal(payload);
      openConfirmNotification("Success");
      refetchCurrent();
    } catch (e) {
      openErrorNotification(
        e?.response?.data?.message || "Something went wrong",
      );
    }
    setRenewalTarget(null);
  };

  const logQuery = new URLSearchParams({
    limit: 20,
    sort_by: "LogNo",
    order: "desc",
    DeviceNo: deviceNo,
  }).toString();
  const { data: logData, loading: logLoading } = useGet(
    deviceNo && tabValue === "deviceLog"
      ? `${process.env.REACT_APP_API_URL}/api/devicelogs/?${logQuery}`
      : null,
  );
  const { data: orderData, loading: orderLoading } = useGet(
    deviceNo && tabValue === "order"
      ? `${process.env.REACT_APP_API_URL}/api/orders/${deviceNo}/device-orders/`
      : null,
  );
  const {
    data: currentOrderData,
    loading: currentOrderLoading,
    refetch: refetchCurrent,
  } = useGet(
    deviceNo && tabValue === "currentOrder"
      ? `${process.env.REACT_APP_API_URL}/api/orders/${deviceNo}/device-current-orders/`
      : null,
  );

  const onTabChange = (value) => {
    setTabValue(value);
    setExpandedRowKeys([]);
  };

  const renderOrdersTable = (key, data, loading, editableRenewal = false) => (
    <GlobalTable
      key={key}
      columns={DeviceOrderItemColumns({ onRenewalToggle, editableRenewal })}
      dataSource={data?.data?.map((el) => ({
        ...el,
        key: el.OrderNo,
      }))}
      loading={loading}
      expandable={{
        expandedRowKeys,
        rowExpandable: (record) =>
          Array.isArray(record?.OrderItems) && record.OrderItems.length > 0,
        expandedRowRender: (record) => (
          <div className="pl-8 pr-2 py-1">
            <GlobalTable
              columns={DeviceOrderItemChildColumns}
              dataSource={record.OrderItems.map((item, index) => ({
                ...item,
                key: item.ItemNo ?? index,
              }))}
              pagination={false}
              tableProps={{ size: "small" }}
            />
          </div>
        ),
        onExpand: (expanded, record) =>
          setExpandedRowKeys((prev) =>
            expanded
              ? [...prev, record.OrderNo]
              : prev.filter((k) => k !== record.OrderNo),
          ),
        columnWidth: 40,
      }}
    />
  );

  return (
    <div className="mt-4 border bg-white rounded-[4px] overflow-hidden">
      <div className="bg-gray-200">
        <div className="py-[5.748px] px-2.5 custom-segmented">
          <Segmented
            options={tabs}
            size="middle"
            value={tabValue}
            onChange={onTabChange}
            className="!bg-transparent !text-black"
          />
        </div>
        {tabValue === "deviceLog" ? (
          <GlobalTable
            key="deviceLog"
            columns={DeviceLogItemColumns()}
            dataSource={logData?.data?.map((el, index) => ({
              ...el,
              key: el.LogNo ?? index,
            }))}
            loading={logLoading}
          />
        ) : tabValue === "order" ? (
          renderOrdersTable("order", orderData, orderLoading)
        ) : (
          renderOrdersTable(
            "currentOrder",
            currentOrderData,
            currentOrderLoading,
            true,
          )
        )}
      </div>
      <ConfirmModal
        showModal={!!renewalTarget}
        setShowModal={(open) => {
          if (!open) setRenewalTarget(null);
        }}
        onDeleteConfirm={onRenewalConfirm}
        handleIgnore={() => setRenewalTarget(null)}
        content={
          renewalTarget?.checked
            ? "Are you sure you want to turn on renewal for this item?"
            : "Are you sure you want to cancel renewal for this item?"
        }
      />
    </div>
  );
};
